import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import {
  LayoutDashboard,
  FilePlus2,
  History,
  List,
  BarChart3,
  SlidersHorizontal,
  TriangleAlert,
  CircleDollarSign,
  ShoppingCart,
  Receipt,
  Ticket,
  User,
  LogOut,
  ChevronUp,
  ChevronDown,
  CarFront,
  Mail,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { portalRoutes } from '../../lib/portalRoutes';
import { getPortalDesignBootstrap } from '../../lib/portalDesignBootstrap';

export const Sidebar = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const design = getPortalDesignBootstrap();

  const [tuningOpen, setTuningOpen] = useState(true);
  const [creditsOpen, setCreditsOpen] = useState(true);

  const labels = {
    de: {
      dashboard: 'Dashboard',
      tuningFile: 'Tuning File',
      newOrder: 'Neuer Auftrag',
      allOrders: 'Alle Aufträge',
      priceList: 'Preisliste',
      configurator: 'Konfigurator',
      credits: 'Credits',
      buy: 'Kaufen',
      invoices: 'Rechnungen',
      tickets: 'Tickets',
      profile: 'Profil',
      emailTemplates: 'E-Mail Templates',
      statistics: 'Statistiken',
      dtcCodes: 'DTC Codes',
      logout: 'Abmelden',
    },
    en: {
      dashboard: 'Dashboard',
      tuningFile: 'Tuning File',
      newOrder: 'New Order',
      allOrders: 'All Orders',
      priceList: 'Price List',
      configurator: 'Configurator',
      credits: 'Credits',
      buy: 'Buy',
      invoices: 'Invoices',
      tickets: 'Tickets',
      profile: 'Profile',
      emailTemplates: 'Email Templates',
      statistics: 'Statistics',
      dtcCodes: 'DTC Codes',
      logout: 'Logout',
    },
  };

  const t = (key) => labels[language]?.[key] || labels.en[key];

  const isPathActive = (path) => location.pathname === path;
  const isGroupActive = (paths) => paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  const NavItem = ({ path, icon: Icon, label, indent = false }) => {
    const isActive = isPathActive(path);

    return (
      <a
        href={path}
        data-testid={`nav-${path.replace('/', '') || 'dashboard'}`}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 rounded-[4px] text-sm transition-colors duration-200',
          indent && 'pl-3',
          isActive
            ? 'bg-primary text-primary-foreground font-semibold'
            : 'text-[#C0C0C2] hover:text-white hover:bg-white/8 font-medium'
        )}
      >
        <Icon strokeWidth={isActive ? 2.5 : 2} className="w-5 h-5 flex-shrink-0" />
        <span>{label}</span>
      </a>
    );
  };

  const MenuGroup = ({ icon: Icon, label, isOpen, onToggle, children, activePaths }) => {
    const hasActiveChild = isGroupActive(activePaths);

    return (
      <div>
        <button
          onClick={onToggle}
          aria-expanded={isOpen}
          className={cn(
            'flex items-center justify-between w-full px-4 py-2.5 rounded-[4px] text-sm transition-colors duration-200',
            hasActiveChild ? 'text-white font-semibold' : 'text-[#C0C0C2] hover:text-white hover:bg-white/8 font-medium'
          )}
        >
          <span className="flex items-center gap-3">
            <Icon strokeWidth={hasActiveChild ? 2.5 : 2} className="w-5 h-5 flex-shrink-0" />
            {label}
          </span>
          <div className="transition-transform duration-200" style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)' }}>
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
        {isOpen && (
          <div className="mt-1 space-y-1 ml-4 pl-3" style={{ borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  const Separator = () => <div className="my-3 mx-4 h-px bg-white/6" />;

  return (
    <aside
      className="fixed left-0 top-0 h-screen w-64 border-r flex flex-col z-50"
      data-testid="sidebar"
      style={{ backgroundColor: '#1C1B1B', borderColor: 'rgba(255,255,255,0.06)' }}
    >
      <div className="h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        {design.logoUrl
          ? <img src={design.logoUrl} alt={design.portalName} className="h-8 max-w-[160px] object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
          : <span className="text-white font-bold text-sm">{design.portalName}</span>
        }
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <NavItem path={portalRoutes.dashboard} icon={LayoutDashboard} label={t('dashboard')} />
        <Separator />

        <MenuGroup
          icon={CarFront}
          label={t('tuningFile')}
          isOpen={tuningOpen}
          onToggle={() => setTuningOpen(!tuningOpen)}
          activePaths={[portalRoutes.order, portalRoutes.orders, portalRoutes.priceList, portalRoutes.statistics]}
        >
          <NavItem path={portalRoutes.order} icon={FilePlus2} label={t('newOrder')} indent />
          <NavItem path={portalRoutes.orders} icon={History} label={t('allOrders')} indent />
          <NavItem path={portalRoutes.priceList} icon={List} label={t('priceList')} indent />
          <NavItem path={portalRoutes.statistics} icon={BarChart3} label={t('statistics')} indent />
        </MenuGroup>

        <Separator />

        <NavItem path={portalRoutes.configurator} icon={SlidersHorizontal} label={t('configurator')} />
        <NavItem path={portalRoutes.dtcCodes} icon={TriangleAlert} label={t('dtcCodes')} />

        <Separator />

        <MenuGroup
          icon={CircleDollarSign}
          label={t('credits')}
          isOpen={creditsOpen}
          onToggle={() => setCreditsOpen(!creditsOpen)}
          activePaths={[portalRoutes.credits, portalRoutes.invoices]}
        >
          <NavItem path={portalRoutes.credits} icon={ShoppingCart} label={t('buy')} indent />
          <NavItem path={portalRoutes.invoices} icon={Receipt} label={t('invoices')} indent />
        </MenuGroup>

        <Separator />

        <NavItem path={portalRoutes.tickets} icon={Ticket} label={t('tickets')} />
        <NavItem path={portalRoutes.emailTemplates} icon={Mail} label={t('emailTemplates')} />
        <NavItem path={portalRoutes.profile} icon={User} label={t('profile')} />
      </nav>

      <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <button
          onClick={() => {
            window.location.href = portalRoutes.logout;
          }}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-4 py-2.5 w-full rounded-[4px] font-medium text-sm text-[#C0C0C2] hover:text-white hover:bg-white/8 transition-colors duration-200"
        >
          <LogOut strokeWidth={2} className="w-5 h-5 flex-shrink-0" />
          {t('logout')}
        </button>
      </div>
    </aside>
  );
};
